import { useCallback } from "react";
import { useAuthStore } from "@/stores/auth.store";
import type { RoleType, Permission, PermissionRequirement, UserRolePermission } from "@/types/rbac.types";

const EMPTY_ROLES: UserRolePermission[] = [];

// ---------------------------------------------------------------------------
// Core check function (pura, sem hooks — usavel em qualquer lugar)
// ---------------------------------------------------------------------------

export function checkPermission(
  userRoles: UserRolePermission[],
  requirements: PermissionRequirement | PermissionRequirement[],
): boolean {
  if (!userRoles.length) return false;

  const reqs = Array.isArray(requirements) ? requirements : [requirements];
  if (!reqs.length) return true;

  // Basta ter UMA das requirements (OR)
  return reqs.some((req) =>
    userRoles.some(
      (ur) =>
        ur.role === req.role && (!req.permission || ur.permission === req.permission),
    ),
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Retorna true se o user tem pelo menos uma das roles/permissions exigidas.
 *
 * @example
 * const canManageUsers = useHasPermission({ role: "ADMINISTRACAO" });
 * const canViewOrManage = useHasPermission([
 *   { role: "BACKOFFICE" },
 *   { role: "ADMINISTRACAO" },
 * ]);
 */
export function useHasPermission(
  requirements: PermissionRequirement | PermissionRequirement[],
): boolean {
  const roles = useAuthStore((s) => s.user?.roles ?? EMPTY_ROLES);
  return checkPermission(roles, requirements);
}

/**
 * Retorna true se o user tem a role especificada (qualquer permission).
 *
 * @example
 * const isAdmin = useHasRole("ADMINISTRACAO");
 */
export function useHasRole(role: RoleType): boolean {
  return useHasPermission({ role });
}

/**
 * Retorna um callback reutilizavel pra checar permissions imperatively.
 *
 * @example
 * const can = useCanCheck();
 * if (can({ role: "ADMINISTRACAO" })) { ... }
 */
export function useCanCheck() {
  const roles = useAuthStore((s) => s.user?.roles ?? EMPTY_ROLES);
  return useCallback(
    (requirements: PermissionRequirement | PermissionRequirement[]) =>
      checkPermission(roles, requirements),
    [roles],
  );
}

/**
 * Retorna as roles do user atual.
 */
export function useUserRoles(): UserRolePermission[] {
  return useAuthStore((s) => s.user?.roles ?? EMPTY_ROLES);
}

/**
 * Retorna true se o user é super admin (ADMINISTRACAO com FULL_ACCESS).
 */
export function useIsSuperAdmin(): boolean {
  return useHasPermission({ role: "ADMINISTRACAO" as RoleType, permission: "FULL_ACCESS" as Permission });
}
